import { z } from "zod";

export type PaymentMode =
  | "free"
  | "fixed"
  | "donation"
  | "sliding_scale"
  | "pwyc";

export const PAYMENT_MODES = [
  "free",
  "fixed",
  "donation",
  "sliding_scale",
  "pwyc",
] as const;

export const PAYMENT_MODE_LABELS: Record<PaymentMode, string> = {
  free: "Free RSVP",
  fixed: "Fixed price",
  donation: "Donation",
  sliding_scale: "Sliding scale",
  pwyc: "Pay what you can",
};

// ─── Currencies ──────────────────────────────────────────────────────────────
//
// Two distinct concepts:
//   • Charge currency  — what Paystack actually charges and settles, and the
//     unit every *_cents column is stored in. Must be a Paystack-supported
//     currency. ALL amounts are stored in the smallest unit ×100, including
//     XOF (which has no subunit — Paystack still expects amount ×100).
//   • Display currency — what the buyer/organiser sees. Defaults to the charge
//     currency. The only cross-currency case is EUR shown over an XOF charge,
//     bridged by the FIXED legal peg 1 EUR = 655.957 XOF (a constant, never a
//     live rate). EUR is presentation-only: the money-integrity path (Paystack
//     amount + webhook cross-check) is always in the charge currency.

export const CHARGE_CURRENCIES = [
  "ZAR",
  "NGN",
  "GHS",
  "USD",
  "KES",
  "XOF",
] as const;
export type ChargeCurrency = (typeof CHARGE_CURRENCIES)[number];

/** Display currencies = every charge currency plus EUR (over XOF only). */
export type DisplayCurrency = ChargeCurrency | "EUR";

export function isChargeCurrency(v: string): v is ChargeCurrency {
  return (CHARGE_CURRENCIES as readonly string[]).includes(v);
}

type CurrencyMeta = {
  /** ISO 4217 code passed to Intl.NumberFormat. */
  code: DisplayCurrency;
  /** Locale used for formatting (symbol placement, grouping). */
  locale: string;
  /** Subunit digits shown. XOF has no subunit → 0. */
  decimals: 0 | 2;
};

export const CURRENCY_META: Record<DisplayCurrency, CurrencyMeta> = {
  ZAR: { code: "ZAR", locale: "en-ZA", decimals: 2 },
  NGN: { code: "NGN", locale: "en-NG", decimals: 2 },
  GHS: { code: "GHS", locale: "en-GH", decimals: 2 },
  USD: { code: "USD", locale: "en-US", decimals: 2 },
  KES: { code: "KES", locale: "en-KE", decimals: 2 },
  XOF: { code: "XOF", locale: "fr-CI", decimals: 0 },
  EUR: { code: "EUR", locale: "en-IE", decimals: 2 },
};

/** Currency names shown in the editor picker. */
export const CHARGE_CURRENCY_LABELS: Record<ChargeCurrency, string> = {
  ZAR: "South African Rand (ZAR)",
  NGN: "Nigerian Naira (NGN)",
  GHS: "Ghanaian Cedi (GHS)",
  USD: "US Dollar (USD)",
  KES: "Kenyan Shilling (KES)",
  XOF: "West African CFA franc (XOF)",
};

// Fixed legal pegs against a charge currency. Keyed "DISPLAY->CHARGE".
// XOF is pegged to the euro at exactly 655.957 — set by treaty, never floats.
const PEG: Record<string, number> = {
  "EUR->XOF": 655.957,
};

/**
 * Is this display currency valid over the given charge currency?
 * Same-currency is always valid; cross-currency only via a known peg.
 */
export function isValidDisplayFor(
  display: DisplayCurrency,
  charge: ChargeCurrency
): boolean {
  if (display === charge) return true;
  return `${display}->${charge}` in PEG;
}

/**
 * Generic money formatter. `cents` is in the smallest unit of `charge`
 * (×100, including XOF). When `display` differs from `charge`, the amount is
 * converted through the fixed peg and shown in the display currency.
 */
export function formatMoney(
  cents: number | null | undefined,
  charge: ChargeCurrency,
  display: DisplayCurrency = charge
): string {
  const meta = CURRENCY_META[display] ?? CURRENCY_META[charge];
  const major = toDisplayMajor(cents ?? 0, charge, display);
  return new Intl.NumberFormat(meta.locale, {
    style: "currency",
    currency: meta.code,
    minimumFractionDigits: meta.decimals === 0 || major % 1 === 0 ? 0 : 2,
    maximumFractionDigits: meta.decimals,
  }).format(major);
}

/**
 * Charge-currency cents → the major-unit number in the display currency.
 * e.g. 3_279_800 XOF-cents with display EUR → 50.00.
 */
export function toDisplayMajor(
  cents: number,
  charge: ChargeCurrency,
  display: DisplayCurrency
): number {
  const major = (cents || 0) / 100; // smallest-unit → major unit of charge ccy
  if (display === charge) return major;
  const rate = PEG[`${display}->${charge}`];
  if (!rate) return major; // unknown pairing → show charge amount untouched
  return major / rate;
}

/**
 * Charge-currency cents → the bare numeric string to seed an input in the
 * display currency (no symbol/grouping). EUR is rounded to 2 dp; XOF to whole.
 */
export function displayInputString(
  cents: number | null | undefined,
  charge: ChargeCurrency,
  display: DisplayCurrency = charge
): string {
  if (cents == null) return "";
  const major = toDisplayMajor(cents, charge, display);
  const decimals = CURRENCY_META[display]?.decimals ?? 2;
  if (decimals === 0) return String(Math.round(major));
  // Trim trailing zeros: 50.00 → "50", 49.50 → "49.5"
  return String(Number(major.toFixed(2)));
}

/**
 * Parse a human-typed amount in `display` currency into charge-currency cents.
 * Strips currency symbols/spaces/grouping. Returns null for unparseable/negative.
 * For EUR-over-XOF: 50 → 50 × 655.957 = 32 798 francs → 3 279 800 stored.
 */
export function parseMoneyToChargeCents(
  raw: string | null | undefined,
  charge: ChargeCurrency,
  display: DisplayCurrency = charge
): number | null {
  if (raw == null) return null;
  const cleaned = String(raw)
    .replace(/[^\d.,-]/g, "") // drop symbols/letters/whitespace
    .replace(/,/g, "") // grouping separators (we treat "." as decimal)
    .trim();
  if (cleaned === "") return null;
  const num = Number(cleaned);
  if (!Number.isFinite(num) || num < 0) return null;

  if (display === charge) {
    return Math.round(num * 100);
  }
  const rate = PEG[`${display}->${charge}`];
  if (!rate) return Math.round(num * 100);
  // num is in display major units → charge major → charge smallest unit (×100)
  const chargeMajor = Math.round(num * rate); // whole francs for XOF
  return chargeMajor * 100;
}

// ─── ZAR shims (deprecated) ──────────────────────────────────────────────────
// Retained so any not-yet-migrated call site keeps compiling. Prefer
// formatMoney / parseMoneyToChargeCents with an explicit currency.

/** @deprecated use {@link formatMoney} with an explicit currency. */
export function formatZAR(cents: number | null | undefined): string {
  return formatMoney(cents, "ZAR");
}

/** @deprecated use {@link parseMoneyToChargeCents} with an explicit currency. */
export function parseZARInput(raw: string | null | undefined): number | null {
  return parseMoneyToChargeCents(raw, "ZAR");
}

// Sliding-scale tier shape: { label: "Sustainer", amount_cents: 80000 }
// A tier priced at 0 is a free tier: attendees who pick it register through
// the free RSVP path with no Paystack involvement (see choiceRequiresPayment).
export const slidingTierSchema = z.object({
  label: z.string().min(1).max(80),
  // Ceiling generous enough for low-unit currencies (XOF stored ×100).
  amount_cents: z.number().int().min(0).max(100_000_000_00),
});
export type SlidingTier = z.infer<typeof slidingTierSchema>;

export const slidingTiersSchema = z.array(slidingTierSchema).min(2).max(5);

type OfferingPaymentConfig = {
  payment_mode: PaymentMode;
  price_cents: number | null;
  suggested_cents: number | null;
  min_cents: number | null;
  sliding_tiers: SlidingTier[] | null;
  /** Charge currency the *_cents are expressed in. Defaults to ZAR. */
  currency?: ChargeCurrency;
  /** Currency to render amounts in user-facing errors. Defaults to charge. */
  display_currency?: DisplayCurrency;
};

export type AmountValidation =
  | { ok: true; amount_cents: number; tier_label?: string }
  | { ok: false; error: string };

/**
 * Validates the amount the attendee chose against the offering's payment mode rules.
 * - fixed: must equal price_cents
 * - donation/pwyc: must be >= min_cents (if set), positive
 * - sliding_scale: must match one of the configured tiers exactly
 * - free: amount must be 0/null (caller should skip this for free)
 */
export function validateAmountForMode(
  cents: number | null,
  config: OfferingPaymentConfig
): AmountValidation {
  switch (config.payment_mode) {
    case "free":
      return { ok: true, amount_cents: 0 };

    case "fixed": {
      const expected = config.price_cents ?? 0;
      if (cents !== expected) {
        return {
          ok: false,
          error: "Amount doesn't match the offering's price",
        };
      }
      if (expected <= 0) {
        return { ok: false, error: "This offering hasn't set a price yet" };
      }
      return { ok: true, amount_cents: expected };
    }

    case "donation":
    case "pwyc": {
      if (cents == null || cents <= 0) {
        return {
          ok: false,
          error: "Please enter an amount",
        };
      }
      if (config.min_cents != null && cents < config.min_cents) {
        const charge = config.currency ?? "ZAR";
        return {
          ok: false,
          error: `Minimum is ${formatMoney(config.min_cents, charge, config.display_currency ?? charge)}`,
        };
      }
      return { ok: true, amount_cents: cents };
    }

    case "sliding_scale": {
      const tiers = config.sliding_tiers ?? [];
      if (tiers.length === 0) {
        return {
          ok: false,
          error: "This offering hasn't set up sliding-scale tiers yet",
        };
      }
      const match = tiers.find((t) => t.amount_cents === cents);
      if (!match) {
        return {
          ok: false,
          error: "Choose one of the offered tiers",
        };
      }
      return { ok: true, amount_cents: match.amount_cents, tier_label: match.label };
    }
  }
}

/** A non-zero payment mode? Used to gate Paystack flows. */
export function isPaidMode(mode: PaymentMode): boolean {
  return mode !== "free";
}

/**
 * Does this particular choice need money to move? The mode alone isn't
 * enough: a donation left at zero and a sliding-scale tier priced at zero are
 * both registered through the free RSVP path, never through Paystack.
 *
 * `cents` is the attendee's chosen amount in charge cents, or null when they
 * haven't chosen yet. For sliding scale a null choice still counts as
 * requiring payment so the paid path can raise "choose a tier" – only an
 * explicit zero tier is free.
 */
export function choiceRequiresPayment(
  mode: PaymentMode,
  cents: number | null | undefined
): boolean {
  switch (mode) {
    case "free":
      return false;
    case "donation":
      return cents != null && cents > 0;
    case "sliding_scale":
      return cents !== 0;
    case "fixed":
    case "pwyc":
      return true;
  }
}
